/**
 * HoverPreviewBadge — internal helper used by the four entity badge
 * hydration wrappers to render an `EntityBadge` wrapped in
 * `EntityBadgeWithHoverPreview`.
 *
 * The wrapper itself doesn't take `entityId` / `orgId`. This helper resolves
 * the `CrmObject` from the store via `subscribeToStoredObject` (which works
 * on both COMPLETE and SUMMARY entries — the popover body only needs basic
 * identity fields, so a summary is good enough; full hydration is unnecessary
 * and would block the popover from rendering for participants loaded via
 * association resolution). It then dispatches the body via `renderHoverBody`.
 *
 * Hover preview is currently gated to the entity types in
 * `HOVER_PREVIEW_TYPES` (contacts, companies). Other
 * types render as bare `EntityBadge` with no popover. When `entityType`
 * isn't yet known (single-badge wrappers during snapshot load), the helper
 * falls through to the wrapped path so the popover infrastructure is ready
 * by the time the type resolves; the wrapper self-suppresses on
 * `state.kind === 'loading'` in that window.
 *
 * Not exported from the barrel — this is an internal building block for
 * `EntityBadgeById`, `EntityBadgeByIds`, `EntityBadgeByEmail`,
 * `EntityBadgeGroup`. Direct callers should use those four wrappers.
 *
 * See issue #5484 for the integration design.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import { HOVER_PREVIEW_TYPES } from '@/lib/crm/core/entityTypes';
import { ensureEntity } from '@/lib/crm/object-cards/entityFetchBatcher';
import {
  hasCompleteObject,
  resolveEntityId,
  subscribe as subscribeToStore,
  subscribeToStoredObject,
} from '@/lib/crm/store/crmObjectStore';
import type { CrmObject, PropertyValue } from '@/types/crm';

import { EntityBadge, type EntityBadgeProps } from './EntityBadge';
import { EntityBadgeWithHoverPreview } from './EntityBadgeWithHoverPreview';
import { renderHoverBody } from './hoverBodyDispatch';

// Entity types with a hover preview body live in the central
// `HOVER_PREVIEW_TYPES` set so this gate and the dispatch in
// `hoverBodyDispatch.tsx` cannot drift out of sync.

// Synthetic-id prefix for unresolved-email participant pills (see
// `ParticipantPillList`). Such ids never map to a real CRM entity, so the
// hover popover is suppressed for them.
const UNRESOLVED_ID_PREFIX = 'unresolved:';

export interface HoverPreviewBadgeProps extends EntityBadgeProps {
  /**
   * The resolved entity id. When omitted (or when `orgId` is omitted), the
   * helper renders a bare `EntityBadge` with no hover preview wrapping.
   */
  entityId?: string;
  /**
   * The current org. When omitted (or when `entityId` is omitted), the
   * helper renders a bare `EntityBadge` with no hover preview wrapping.
   */
  orgId?: number;
  /**
   * Entity type. When supplied AND the type isn't in
   * `HOVER_PREVIEW_TYPES`, the helper renders a bare `EntityBadge` with
   * no popover. When omitted (e.g. during snapshot load in a single-badge
   * wrapper), the helper falls through to the wrapped path so the popover
   * is ready by the time the type resolves.
   */
  entityType?: string;
}

export function HoverPreviewBadge({
  entityId,
  orgId,
  entityType,
  ...badgeProps
}: HoverPreviewBadgeProps): React.ReactElement {
  // When either id or orgId is missing, hover preview cannot be wired up —
  // render the bare badge so existing legacy callers without orgId stay
  // unaffected.
  if (entityId === undefined || orgId === undefined) {
    return <EntityBadge {...badgeProps} />;
  }

  // Unresolved-email participant pills carry a synthetic id
  // (`unresolved:<email>`). They represent an address not yet promoted to a
  // CRM entity, so there is no entity to preview — and the contact hover body
  // would surface Draft-email / Add-note actions bound to the bogus synthetic
  // id, with no Create Contact affordance. Render the bare badge; the pill's
  // own onClick still routes to the Create Contact modal.
  if (entityId.startsWith(UNRESOLVED_ID_PREFIX)) {
    return <EntityBadge {...badgeProps} />;
  }

  // When the entity type is known and not in the supported set, skip the
  // popover wrapping entirely. Hover does nothing for these badges. When
  // entityType is undefined (e.g. snapshot still loading), fall through to
  // the wrapped path; the wrapper self-suppresses on loading state.
  if (entityType !== undefined && !HOVER_PREVIEW_TYPES.has(entityType)) {
    return <EntityBadge {...badgeProps} />;
  }

  return (
    <HoverPreviewBadgeInner {...badgeProps} entityId={entityId} orgId={orgId} />
  );
}

interface HoverPreviewBadgeInnerProps extends EntityBadgeProps {
  entityId: string;
  orgId: number;
}

/**
 * Inner component that always has `entityId` / `orgId` defined so the
 * subscription hooks are unconditional (Rules of Hooks).
 *
 * Reads via `subscribeToStoredObject` rather than `useCrmObjectSnapshot`
 * because we want SUMMARY entries to feed the popover too. Email-thread
 * participants and association-resolved entities land in the store as
 * summaries (isComplete=false); `useCrmObjectSnapshot` silently returns
 * `undefined` for those, and `renderHoverBody(undefined, ...)` produces a
 * `null` body — the popover renders blank. Summary entries carry the
 * identity fields the body needs (display name, email), so they're a
 * fine source for the hover preview.
 *
 * Still triggers `ensureEntity` so an in-flight upgrade to a complete
 * entry runs alongside — when the upgrade lands, the popover re-renders
 * with the richer data (job title, company, etc. on contact previews).
 */
function HoverPreviewBadgeInner({
  entityId,
  orgId,
  ...badgeProps
}: HoverPreviewBadgeInnerProps): React.ReactElement {
  // Resolve through the alias map so pre-merge ids surface the canonical
  // entry. This mirrors what `useStoreSelector` does for association list
  // reads — without it, a chip created for an association target that has
  // since been merged looks up the wrong key in `objects` and the popover
  // renders empty.
  //
  // We subscribe via `useSyncExternalStore` but make the snapshot the
  // resolved canonical id (a string), not the full store. With identity-
  // equality on a primitive, React skips re-renders for store mutations
  // that don't change THIS entity's canonical id — long lists of hover
  // badges no longer re-render on every CRM store mutation.
  const getCanonicalId = useCallback(
    () => resolveEntityId(entityId),
    [entityId]
  );
  const canonicalId = useSyncExternalStore(subscribeToStore, getCanonicalId);

  // Kick off (or no-op) a fetch in the background. Cheap when the batcher
  // is already hot from the parent wrapper. Synthetic unresolved-email ids
  // are already short-circuited to a bare badge in the outer
  // `HoverPreviewBadge`, so they never reach this inner component.
  useEffect(() => {
    if (orgId > 0 && !hasCompleteObject(canonicalId)) {
      ensureEntity(orgId, canonicalId);
    }
  }, [orgId, canonicalId]);

  const sub = useMemo(
    () => subscribeToStoredObject(canonicalId),
    [canonicalId]
  );
  const subscribe = useCallback((cb: () => void) => sub.subscribe(cb), [sub]);
  const getSnapshot = useCallback(() => sub.getSnapshot(), [sub]);
  const stored = useSyncExternalStore(subscribe, getSnapshot);

  // The popover body needs a `CrmObject` to render. During the **race
  // window** — between chip mount and `ensureEntity` flush — the store
  // doesn't yet have the entry, and `renderHoverBody(undefined, ...)`
  // returns `null`, producing a visibly-empty popover frame.
  //
  // Synthesize a minimal CrmObject from the badge data to bridge that
  // window. The hover-body components only consume `entity_id`,
  // `entity_type_name`, and basic identity properties (display name +
  // email) — all of which the parent badge already carries. The store
  // remains the source of truth for later updates: as soon as it has a real
  // entry for `canonicalId`, the `stored?.data ?? fallbackObject` selector
  // flips to the real entity and the synthetic copy is discarded.
  const fallbackObject = useMemo<CrmObject | undefined>(() => {
    if (stored?.data) {
      return undefined;
    }
    if (badgeProps.state.kind !== 'data') {
      return undefined;
    }
    const { data } = badgeProps.state;
    const properties: Record<string, PropertyValue> = {
      ahoy_display_name: { value: data.displayName } as PropertyValue,
    };
    // Thread email through when the badge carries one (resolved person
    // badges pass their primary email). Lets the hover body render an email
    // row during the race window without waiting on store hydration.
    if (data.email) {
      properties.ahoy_email = { value: [data.email] } as PropertyValue;
    }
    return {
      id: canonicalId,
      org_id: orgId,
      entity_id: canonicalId,
      entity_type_name: data.entityType as CrmObject['entity_type_name'],
      properties,
      last_event_seq: 0,
      deleted_at: null,
    };
  }, [stored, badgeProps.state, canonicalId, orgId]);

  const objectForBody = stored?.data ?? fallbackObject;

  return (
    <EntityBadgeWithHoverPreview {...badgeProps}>
      {renderHoverBody(objectForBody, orgId)}
    </EntityBadgeWithHoverPreview>
  );
}
